import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/lib/theme/provider';
import { THEME_REGISTRY } from '@/lib/theme/registry';
import { Check, Monitor, Moon, Sun } from 'lucide-react';

/**
 * The Mode (light/dark/system) + Theme (registered names) DropdownMenu
 * items. Rendered standalone via `ThemeToggle` AND embedded inside
 * `UserMenu`'s dropdown — both paths use the same `useTheme()` actions
 * so behaviour is identical regardless of where the picker lives.
 *
 * No wrapping `DropdownMenuContent` — the items rely on a parent
 * Dropdown to host them. Each `DropdownMenuItem` deliberately uses
 * `onSelect={(e) => e.preventDefault()}` to keep the parent dropdown
 * open after a mode/theme switch, so users can see the result
 * immediately.
 */
export function ThemeMenuItems() {
  const { mode, theme, setMode, setTheme } = useTheme();

  return (
    <>
      <DropdownMenuLabel>Appearance</DropdownMenuLabel>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setMode('light');
        }}
      >
        <Sun className="mr-2 h-4 w-4" aria-hidden="true" />
        Light
        {mode === 'light' ? <Check className="ml-auto h-4 w-4" aria-hidden="true" /> : null}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setMode('dark');
        }}
      >
        <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
        Dark
        {mode === 'dark' ? <Check className="ml-auto h-4 w-4" aria-hidden="true" /> : null}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setMode('system');
        }}
      >
        <Monitor className="mr-2 h-4 w-4" aria-hidden="true" />
        System
        {mode === 'system' ? <Check className="ml-auto h-4 w-4" aria-hidden="true" /> : null}
      </DropdownMenuItem>
      {THEME_REGISTRY.length > 1 ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          {THEME_REGISTRY.map((entry) => (
            <DropdownMenuItem
              key={entry.id}
              onSelect={(e) => {
                e.preventDefault();
                setTheme(entry.id);
              }}
            >
              {entry.label}
              {entry.status === 'preview' ? (
                <span className="ml-2 text-xs text-muted-foreground">(preview)</span>
              ) : null}
              {theme === entry.id ? <Check className="ml-auto h-4 w-4" aria-hidden="true" /> : null}
            </DropdownMenuItem>
          ))}
        </>
      ) : null}
    </>
  );
}
